// Phase NAATI-MAX B2 — /translate/naati/levels
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { NAATI_LEVELS, NAATI_LEVELS_TRANSLATOR, NAATI_LEVELS_INTERPRETER } from "@/data/naati-certification-levels";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/translate/naati/levels`;

const faqs = [
  { q: "CT กับ CAT ต่างกันยังไง?", a: "CT (Certified Translator) เป็นระดับมาตรฐานสำหรับเอกสารราชการ ~90% ของงานยื่นออสเตรเลีย ส่วน CAT (Certified Advanced Translator) คือระดับที่สูงกว่า ผ่านการทดสอบเฉพาะทาง เหมาะกับคู่มือทางการแพทย์ คำพิพากษาศาล สัญญาระหว่างประเทศที่ซับซ้อน." },
  { q: "ตรา NAATI ต้องต่ออายุไหม?", a: "ใช่ — NAATI กำหนดให้นักแปลทุกคนต้องต่ออายุการรับรองทุก 3 ปี โดยส่งหลักฐาน CPD (Continuing Professional Development) + Work Practice. นักแปลที่ตราหมดอายุ ไม่สามารถออกตรา NAATI ที่ใช้ได้กับ DHA." },
  { q: "ล่าม CPI / CI / CSHI ต่างกันยังไง?", a: "CPI เป็นล่ามเริ่มต้นสำหรับงานชุมชน. CI เป็นล่ามมาตรฐานสำหรับงานราชการและธุรกิจ. CSHI/CSLI เป็นล่ามเฉพาะทางการแพทย์/กฎหมาย ที่จำเป็นในศาลและโรงพยาบาลรัฐออสเตรเลีย." },
  { q: "ระดับสูงสุดของ NAATI คืออะไร?", a: "CCI — Certified Conference Interpreter ใช้ในการประชุม APEC, ASEAN, UN, รัฐสภาออสเตรเลีย เป็นระดับที่ทดสอบ Simultaneous Interpreting ในตู้แปล." },
];

export const Route = createFileRoute("/translate/naati/levels")({
  head: () => ({
    meta: [
      { title: ctaTitle("ระดับการรับรอง NAATI — CT, CAT, CPI, CI, CSHI | NYC", "/translate/naati/levels", "th")},
      { name: "description", content: ctaDesc("อธิบายความแตกต่างของระดับการรับรอง NAATI ทั้งฝั่งนักแปล (CT/CAT/RPT) และล่าม (CPI/CI/CSHI/CSLI/CCI) ใช้กับวีซ่าและงานราชการออสเตรเลียระดับไหน. ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา", "/translate/naati/levels", "th")},
      { property: "og:title", content: ctaTitle("ระดับการรับรอง NAATI ทั้งหมด — CT, CAT, CPI, CI, CSHI, CSLI, CCI", "/translate/naati/levels", "th")},
      { property: "og:description", content: ctaDesc("แผนผัง NAATI Certification Levels พร้อมตัวอย่างเอกสารและงานที่ใช้แต่ละระดับ.", "/translate/naati/levels", "th")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/levels", "/en/translate/naati/levels"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        "@id": `${URL}#article`, headline: "ระดับการรับรอง NAATI ทั้งหมด", inLanguage: "th",
        author: { "@type": "Organization", name: "NYC Language Institute" },
        publisher: { "@type": "Organization", name: "NYC Language Institute" },
        datePublished: "2026-06-27", dateModified: "2026-06-27",
        url: URL,
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "th",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL,
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="th"
      eyebrow="Knowledge Base"
      h1="ระดับการรับรอง NAATI ทั้งหมด — เลือกให้ถูก ประหยัดทั้งเงินและเวลา"
      lead="NAATI (National Accreditation Authority for Translators and Interpreters) ของออสเตรเลีย แบ่งการรับรองเป็น 8 ระดับ ฝั่งนักแปล 3 ระดับ (CT/CAT/RPT) และฝั่งล่าม 5 ระดับ (CPI/CI/CSHI/CSLI/CCI) — ใช้ระดับไม่ตรงงาน = ค่าใช้จ่ายเกินจริง หรือเอกสารถูกตีกลับ."
      speakableAnswer="CT ใช้สำหรับเอกสารราชการทั่วไป ~90% ของงานยื่นออสเตรเลีย. CAT ใช้สำหรับเอกสารเฉพาะทางที่ซับซ้อน. ล่าม CSHI/CSLI ใช้ในโรงพยาบาลรัฐและศาลออสเตรเลีย. CCI เป็นระดับสูงสุดสำหรับการประชุมระดับชาติ."
      sections={[
        {
          heading: "ฝั่งนักแปล (Translators) — 3 ระดับ",
          paragraph: "นักแปล NAATI ทุกคนต้องมีหมายเลข CPN ระบุชัดเจนบนตราประทับ และต่ออายุทุก 3 ปี.",
          table: {
            headers: ["ระดับ", "ขอบเขตงาน", "ตัวอย่างเอกสาร"],
            rows: NAATI_LEVELS_TRANSLATOR.map((l) => [
              `${l.code}\n${l.nameTh}`,
              l.scopeTh,
              l.exampleDocsTh.slice(0, 3).join(" • "),
            ]),
          },
        },
        {
          heading: "ฝั่งล่าม (Interpreters) — 5 ระดับ",
          paragraph: "ล่าม NAATI ที่ใช้ในศาลและโรงพยาบาลรัฐออสเตรเลีย ต้องเป็น CSHI หรือ CSLI ขึ้นไป.",
          table: {
            headers: ["ระดับ", "ขอบเขตงาน", "ตัวอย่างงาน"],
            rows: NAATI_LEVELS_INTERPRETER.map((l) => [
              `${l.code}\n${l.nameTh}`,
              l.scopeTh,
              l.exampleDocsTh.slice(0, 3).join(" • "),
            ]),
          },
        },
        {
          heading: "การทดสอบและการต่ออายุ",
          bullets: NAATI_LEVELS.map((l) => `${l.code} — ${l.testedTh}`),
        },
      ]}
      faqs={faqs}
      cta={{ label: "ปรึกษาว่าควรใช้ระดับไหน", href: "/translate/naati" }}
    />
  ),
});
